import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TurnstileWidget } from '@/components/auth/TurnstileWidget';
import { supabase } from '@/integrations/supabase/client';
import { isTurnstileBypassedEnv, verifyTurnstileToken } from '@/lib/turnstile';
import { getReferralCode, clearReferralCode } from '@/hooks/useReferralCapture';

const signupSchema = z.object({
  fullName: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string().min(8),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type SignupFormValues = z.infer<typeof signupSchema>;

export default function Signup() {
  const { t, language } = useLanguage();
  const { signUp, signInWithGoogle, signInWithApple } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileBypassed, setTurnstileBypassed] = useState(false);

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(null), []);
  const handleTurnstileTimeout = useCallback(() => setTurnstileBypassed(true), []);

  const skipTurnstile = isTurnstileBypassedEnv();

  // Redirect referral links to landing page instead of signup
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      // Store ref and redirect to landing page
      localStorage.setItem('sayitnow_ref', ref);
      sessionStorage.setItem('sayitnow_ref', ref);
      navigate(`/?ref=${encodeURIComponent(ref)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema as any),
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const canSubmit = skipTurnstile || turnstileBypassed || !!turnstileToken;

  const onSubmit = async (values: SignupFormValues) => {
    if (!canSubmit) {
      toast({ title: t.common.error, description: t.security.checkRequired, variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      if (turnstileToken && !skipTurnstile) {
        const verification = await verifyTurnstileToken(turnstileToken);
        if (!verification.success) {
          toast({ title: t.common.error, description: verification.reason ?? t.security.verificationFailed, variant: 'destructive' });
          setTurnstileToken(null);
          return;
        }
      }
      const refCode = searchParams.get('ref') || sessionStorage.getItem('sayitnow_ref') || getReferralCode() || undefined;
      const { error } = await signUp(values.email, values.password, values.fullName, refCode);
      if (error) {
        const isExistingAccount = /already registered|already exists|user_already_exists/i.test(error.message);
        toast({
          title: t.common.error,
          description: isExistingAccount ? 'This email already has an account. Please sign in or use Forgot Password.' : error.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: ({ en: 'Account created!', es: '¡Cuenta creada!', fr: 'Compte créé !', pt: 'Conta criada!', ar: 'تم إنشاء الحساب!', ja: 'アカウント作成完了！', ko: '계정이 생성되었습니다!', id: 'Akun dibuat!' } as Record<string, string>)[language] || 'Account created!',
          description: ({ en: 'Welcome to Say It NOW. Redirecting to dashboard...', es: 'Bienvenido a Say It NOW. Redirigiendo al panel...', fr: 'Bienvenue sur Say It NOW. Redirection vers le tableau de bord...', pt: 'Bem-vindo ao Say It NOW. Redirecionando para o painel...', ar: 'مرحباً في Say It NOW. جاري التحويل...', ja: 'Say It NOWへようこそ。ダッシュボードにリダイレクトしています...', ko: 'Say It NOW에 오신 것을 환영합니다. 대시보드로 이동 중...', id: 'Selamat datang di Say It NOW. Mengalihkan ke dasbor...' } as Record<string, string>)[language] || 'Welcome to Say It NOW. Redirecting to dashboard...',
        });
        clearReferralCode();
        // Only honor from=try if there's an actual draft saved
        const fromTry = searchParams.get('from') === 'try' && !!localStorage.getItem('tryDraft');
        navigate(fromTry ? '/create?from=try' : '/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      toast({
        title: t.common.error,
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleAppleSignIn = async () => {
    const { error } = await signInWithApple();
    if (error) {
      toast({
        title: t.common.error,
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex bg-hero-gradient">
      {/* Left side - Decorative */}
      <div className="hidden lg:flex flex-1 bg-warm-gradient items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10" />
        <div className="max-w-md text-center relative z-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <BrandLogo size="lg" className="mx-auto mb-8" />
          </motion.div>
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.hero.tagline}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t.hero.subheadline}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="absolute top-4 right-4">
            <LanguageSelector />
          </div>

          <Link to="/" className="mb-8 inline-block">
            <BrandLogo size="md" />
          </Link>

          <div className="glass rounded-3xl p-8 shadow-glass-lg">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {t.auth.signupTitle}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t.auth.signupSubtitle}
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.fullName}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input placeholder="John Doe" className="pl-10 rounded-xl bg-background/60" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.email}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input placeholder="you@example.com" className="pl-10 rounded-xl bg-background/60" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.password}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            className="pl-10 pr-10 rounded-xl bg-background/60"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.confirmPassword}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            className="pl-10 pr-10 rounded-xl bg-background/60"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!skipTurnstile && !turnstileBypassed && (
                  <TurnstileWidget onVerify={handleTurnstileVerify} onExpire={handleTurnstileExpire} onLoadTimeout={handleTurnstileTimeout} />
                )}

                <Button type="submit" className="w-full rounded-xl h-11" disabled={isLoading || !canSubmit}>
                  {isLoading ? t.common.loading : t.auth.signupButton}
                </Button>
              </form>
            </Form>

            <div className="my-6 flex items-center">
              <div className="flex-1 border-t border-border/50" />
              <span className="px-4 text-sm text-muted-foreground">
                {t.auth.orContinueWith}
              </span>
              <div className="flex-1 border-t border-border/50" />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl bg-background/40 border-border/50"
                onClick={handleGoogleSignIn}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t.auth.google}
              </Button>

              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl bg-background/40 border-border/50"
                onClick={handleAppleSignIn}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t.auth.apple}
              </Button>
            </div>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              {t.auth.hasAccount}{' '}
              <Link to="/login" className="text-secondary hover:underline font-medium">
                {t.nav.login}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
